import React, { useState } from 'react'
import { motion, type PanInfo } from 'framer-motion'
import {
  HeartIcon,
  XMarkIcon,
  StarIcon,
  MapPinIcon,
  UserIcon,
  ChatBubbleLeftIcon,
  AdjustmentsHorizontalIcon,
  FireIcon,
  SparklesIcon
} from '@heroicons/react/24/outline'

const DatingPage: React.FC = () => {
  const [currentCard, setCurrentCard] = useState(0)
  const [showFilters, setShowFilters] = useState(false)

  // Mock user data
  const profiles = [
    {
      id: 1,
      name: "Emma",
      age: 26,
      distance: "2 miles away",
      bio: "Adventure seeker, coffee enthusiast, and weekend hiker. Looking for someone to explore the city with!",
      images: ["/api/placeholder/400/600", "/api/placeholder/400/600"],
      interests: ["Travel", "Photography", "Yoga", "Cooking"],
      compatibility: 89,
      verified: true
    },
    {
      id: 2,
      name: "Alex",
      age: 29,
      distance: "5 miles away",
      bio: "Software engineer by day, chef by night. Love trying new restaurants and making people laugh.",
      images: ["/api/placeholder/400/600"],
      interests: ["Technology", "Cooking", "Comedy", "Music"],
      compatibility: 76,
      verified: false
    },
    {
      id: 3,
      name: "Sofia",
      age: 24,
      distance: "3 miles away",
      bio: "Artist and dreamer. Creating beautiful things and looking for beautiful moments.",
      images: ["/api/placeholder/400/600", "/api/placeholder/400/600", "/api/placeholder/400/600"],
      interests: ["Art", "Design", "Music", "Literature"],
      compatibility: 93,
      verified: true
    }
  ]

  const handleSwipe = (direction: 'left' | 'right') => {
    if (direction === 'right') {
      // Handle like
      console.log('Liked profile:', profiles[currentCard])
    } else {
      // Handle pass
      console.log('Passed profile:', profiles[currentCard])
    }
    
    setCurrentCard(prev => (prev + 1) % profiles.length)
  }

  const handleDragEnd = (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const swipeThreshold = 100
    if (info.offset.x > swipeThreshold) {
      handleSwipe('right')
    } else if (info.offset.x < -swipeThreshold) {
      handleSwipe('left')
    }
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="grid lg:grid-cols-12 gap-8">
        {/* Swipe Cards Section */}
        <div className="lg:col-span-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-display font-bold gradient-text">Discover</h1>
              <p className="text-white/70 mt-2">Find your perfect match</p>
            </div>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowFilters(!showFilters)}
              className="btn-glass"
            >
              <AdjustmentsHorizontalIcon className="h-5 w-5 mr-2" />
              Filters
            </motion.button>
          </div>

          {/* Swipe Stack */}
          <div className="relative h-[600px] flex items-center justify-center">
            {profiles.map((profile, index) => {
              const isActive = index === currentCard
              
              if (index !== currentCard && index !== (currentCard + 1) % profiles.length) {
                return null
              }

              return (
                <motion.div
                  key={profile.id}
                  className={`absolute w-full max-w-sm ${isActive ? 'z-20' : 'z-10'}`}
                  initial={false}
                  animate={{
                    scale: isActive ? 1 : 0.95,
                    y: isActive ? 0 : 10,
                    opacity: isActive ? 1 : 0.5
                  }}
                  drag={isActive ? 'x' : false}
                  dragConstraints={{ left: 0, right: 0 }}
                  dragElastic={0.7}
                  onDragEnd={handleDragEnd}
                  whileDrag={{ rotate: 5 }}
                >
                  <ProfileCard profile={profile} />
                </motion.div>
              )
            })}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-center items-center space-x-8 mt-8">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => handleSwipe('left')}
              className="w-16 h-16 rounded-full bg-gradient-to-r from-red-500/20 to-red-600/20 border border-red-500/30 flex items-center justify-center hover:from-red-500/30 hover:to-red-600/30 transition-all"
            >
              <XMarkIcon className="h-8 w-8 text-red-400" />
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="w-12 h-12 rounded-full bg-gradient-to-r from-accent-500/20 to-accent-600/20 border border-accent-500/30 flex items-center justify-center hover:from-accent-500/30 hover:to-accent-600/30 transition-all"
            >
              <StarIcon className="h-6 w-6 text-accent-400" />
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => handleSwipe('right')}
              className="w-16 h-16 rounded-full bg-gradient-to-r from-primary-500/20 to-secondary-500/20 border border-primary-500/30 flex items-center justify-center hover:from-primary-500/30 hover:to-secondary-500/30 transition-all"
            >
              <HeartIcon className="h-8 w-8 text-primary-400" />
            </motion.button>
          </div>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-4 space-y-6">
          {/* Match Stats */}
          <div className="card-glass p-6">
            <h3 className="text-xl font-bold mb-4">Your Activity</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-white/70">Today's Likes</span>
                <span className="font-semibold text-primary-400">12/50</span>
              </div>
              <div className="w-full bg-white/10 rounded-full h-2">
                <div className="bg-gradient-to-r from-primary-600 to-secondary-600 h-2 rounded-full w-1/4" />
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-white/70">Super Likes</span>
                <span className="font-semibold text-accent-400">3/5</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-white/70">New Matches</span>
                <span className="font-semibold text-secondary-400">8</span>
              </div>
            </div>
          </div>

          {/* Recent Matches */}
          <div className="card-glass p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold">Recent Matches</h3>
              <FireIcon className="h-5 w-5 text-accent-400" />
            </div>
            
            <div className="space-y-3">
              <MatchItem
                name="Jessica"
                time="2 hours ago"
                message="Hey! Love your profile 😊"
              />
              <MatchItem
                name="Michael"
                time="1 day ago"
                message="Nice to match with you!"
              />
              <MatchItem
                name="Luna"
                time="2 days ago"
                message="Let's grab coffee sometime?"
              />
            </div>
            
            <button className="btn-glass w-full mt-4">
              <ChatBubbleLeftIcon className="h-4 w-4 mr-2" />
              View All Matches
            </button>
          </div>

          {/* Premium Upgrade */}
          <div className="card-glass p-6 bg-gradient-to-br from-primary-600/20 to-secondary-600/20">
            <div className="flex items-center mb-4">
              <SparklesIcon className="h-6 w-6 text-accent-400 mr-2" />
              <h3 className="text-xl font-bold">Go Premium</h3>
            </div>
            
            <p className="text-white/70 mb-4">Unlock unlimited likes, see who liked you, and boost your profile!</p>
            
            <ul className="space-y-2 mb-4 text-sm">
              <li className="flex items-center">
                <span className="w-2 h-2 bg-primary-400 rounded-full mr-2"></span>
                Unlimited likes
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-primary-400 rounded-full mr-2"></span>
                See who likes you
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-primary-400 rounded-full mr-2"></span>
                Profile boost
              </li>
            </ul>
            
            <button className="btn-primary w-full">
              Upgrade Now
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

interface ProfileCardProps {
  profile: {
    id: number
    name: string
    age: number
    distance: string
    bio: string
    images: string[]
    interests: string[]
    compatibility: number
    verified: boolean
  }
}

const ProfileCard: React.FC<ProfileCardProps> = ({ profile }) => {
  return (
    <div className="swipe-card bg-dark-100 border border-white/10 overflow-hidden h-[600px]">
      {/* Image Section */}
      <div className="relative h-3/5 bg-gradient-to-br from-primary-500/20 to-secondary-500/20">
        <div className="absolute inset-0 flex items-center justify-center">
          <UserIcon className="h-24 w-24 text-white/30" />
        </div>
        
        {/* Image indicators */}
        {profile.images.length > 1 && (
          <div className="absolute top-4 left-4 right-4 flex space-x-1">
            {profile.images.map((_, index) => (
              <div
                key={index}
                className={`h-1 flex-1 rounded ${
                  index === 0 ? 'bg-white' : 'bg-white/30'
                }`}
              />
            ))}
          </div>
        )}

        {/* Compatibility Badge */}
        <div className="absolute top-4 right-4">
          <div className="bg-primary-500/20 backdrop-blur-md rounded-full px-3 py-1 border border-primary-500/30">
            <span className="text-xs font-medium text-primary-300">
              {profile.compatibility}% match
            </span>
          </div>
        </div>

        {/* Verified Badge */}
        {profile.verified && (
          <div className="absolute bottom-4 right-4">
            <div className="bg-green-500/20 backdrop-blur-md rounded-full p-2 border border-green-500/30">
              <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        )}
      </div>

      {/* Info Section */}
      <div className="p-6 h-2/5 flex flex-col">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-2xl font-bold">{profile.name}, {profile.age}</h2>
        </div>
        
        <div className="flex items-center text-white/70 text-sm mb-3">
          <MapPinIcon className="h-4 w-4 mr-1" />
          {profile.distance}
        </div>
        
        <p className="text-white/80 text-sm mb-4 flex-1 overflow-hidden">
          {profile.bio}
        </p>
        
        <div className="flex flex-wrap gap-2">
          {profile.interests.slice(0, 3).map((interest) => (
            <span
              key={interest}
              className="px-3 py-1 bg-primary-500/20 text-primary-300 text-xs rounded-full border border-primary-500/30"
            >
              {interest}
            </span>
          ))}
          {profile.interests.length > 3 && (
            <span className="px-3 py-1 bg-white/10 text-white/70 text-xs rounded-full">
              +{profile.interests.length - 3}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

interface MatchItemProps {
  name: string
  time: string
  message: string
}

const MatchItem: React.FC<MatchItemProps> = ({ name, time, message }) => (
  <div className="flex items-center space-x-3 p-3 rounded-xl hover:bg-white/5 transition-colors cursor-pointer">
    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-primary-600 to-secondary-600 p-0.5">
      <div className="w-full h-full rounded-full bg-dark-100 flex items-center justify-center">
        <UserIcon className="h-5 w-5 text-white/50" />
      </div>
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between">
        <p className="font-medium text-white truncate">{name}</p>
        <p className="text-xs text-white/50">{time}</p>
      </div>
      <p className="text-sm text-white/70 truncate">{message}</p>
    </div>
  </div>
)

export default DatingPage